import { API_URL, STREAM_API_URL, apiCall } from '../../config/api';
import { getAuthHeader } from '../../config/auth';
import type { AgentChatRequestPayload } from './agentSurfaceTypes';
import { captureProductEvent } from '../../lib/posthog';

interface CreateAgentConversationInput {
  title: string;
  mode: string;
  template_id?: string;
}

interface UpdateAgentConversationInput {
  title?: string;
  context?: Record<string, unknown>;
  thumbnailData?: string;
  expectedContextVersion?: number;
}

export async function createAgentConversation({ title, mode, template_id }: CreateAgentConversationInput) {
  captureProductEvent('agent_create_requested', {
    agent_profile: String(mode || 'unknown').slice(0, 64),
    route_family: 'agent',
  });
  return apiCall('/api/chat/conversations', {
    method: 'POST',
    body: JSON.stringify({ title, mode, ...(template_id ? { template_id } : {}) }),
  });
}

export async function fetchAgentConversation(conversationId: string) {
  return apiCall(`/api/chat/conversations/${conversationId}`);
}

/** Presentation metadata must not rewrite the revision-bound artifact. */
export async function updateConversationPresentationThumbnail(conversationId: string, thumbnailData: string) {
  const response = await apiCall(`/api/chat/conversations/${conversationId}/presentation-thumbnail`, {
    method: 'PUT',
    body: JSON.stringify({ thumbnail_data: thumbnailData }),
  });
  if (response?.status !== 'success' || response.conversation_id !== conversationId
    || typeof response.thumbnail_url !== 'string' || !response.thumbnail_url.trim()) {
    throw new Error('presentation_thumbnail_update_not_confirmed');
  }
  return response;
}

export async function updateAgentConversation(
  conversationId: string,
  { title, context, thumbnailData, expectedContextVersion }: UpdateAgentConversationInput,
) {
  const response = await apiCall(`/api/chat/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      ...(title !== undefined ? { title } : {}),
      ...(context !== undefined ? { context } : {}),
      ...(thumbnailData ? { thumbnail_data: thumbnailData } : {}),
      ...(expectedContextVersion !== undefined
        ? { expected_context_version: expectedContextVersion }
        : {}),
    }),
  });
  // PATCH success is an explicit backend acknowledgement. The shared helper
  // returns null for every unconfirmed outcome, so callers must not advance a
  // clean marker or report "saved" unless this exact acknowledgement arrived.
  if (!response || response.status !== 'success') {
    throw new Error('agent_conversation_update_not_confirmed');
  }
  return response;
}

export async function fetchAgentConversationHistory(conversationId: string) {
  return apiCall(`/api/chat/history?conversation_id=${conversationId}`);
}

export type DurableApplicationDelivery = {
  delivery_id: string;
  change_id: string;
  turn_id: string;
  conversation_id: string;
  profile_key: string;
  artifact_id: string | null;
  base_revision: number | string | null;
  revision_fingerprint: string;
  affected_item_ids: string[];
  status: 'pending' | 'claimed' | 'stale' | 'conflicted' | 'applied' | 'failed' | 'cancelled';
  error_code?: string | null;
  change_application?: Record<string, any>;
  claim_token?: string;
  claim_expires_at?: string;
  expected_context_version?: number;
  settlement_acknowledged?: boolean;
  review_followup?: {change_id:string;status:string;reason_code?:string|null} | null;
};

export async function claimDurableApplicationDelivery(
  conversationId: string,
  currentRevision: number | string | null,
): Promise<{ delivery: DurableApplicationDelivery | null; turn_in_progress?: boolean } | null> {
  return apiCall(`/api/application-deliveries/conversations/${conversationId}/claim`, {
    method: 'POST',
    body: JSON.stringify({ current_revision: currentRevision }),
  });
}

export async function acknowledgeDurableApplicationDelivery(
  deliveryId: string,
  payload: {
    application_token?: string;
    claim_token?: string;
    settlement_token?: string;
    status: 'failed';
    error_code: string;
  },
) {
  return apiCall(`/api/application-deliveries/${deliveryId}/ack`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function releaseDurableApplicationDeliveryClaim(
  deliveryId: string,
  payload: {
    claim_token: string;
    reason_code:
      | 'editor_busy'
      | 'editor_unmounted'
      | 'revision_changed'
      | 'unsupported_surface';
  },
) {
  const response = await apiCall(`/api/application-deliveries/${deliveryId}/release`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response || !['pending', 'applied'].includes(response.status)) {
    throw new Error('application_delivery_claim_release_not_confirmed');
  }
  return response as DurableApplicationDelivery;
}

export class DurableApplicationCommitError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number | null,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'DurableApplicationCommitError';
  }

  readonly preserveAppliedState = this.retryable;
}

/** Bounded server reason passed back through the application-settlement
 * channel. Unknown client exceptions never become unvalidated wire values. */
export const durableApplicationFailureCode = (error: unknown): string => (
  error instanceof DurableApplicationCommitError
  && /^[a-z][a-z0-9_]{0,79}$/.test(error.code)
    ? error.code
    : 'durable_application_rejected'
);

/** One product artifact per commit, named by its product: `deck` for Slides,
 * `project` for Design. The server requires exactly one, so the type does too
 * rather than letting a cast promise a field the body does not carry. */
type DurableApplicationArtifactPayload =
  | { deck: Record<string, unknown>; project?: never }
  | { project: Record<string, unknown>; deck?: never };

export async function applyDurableApplicationDelivery(
  deliveryId: string,
  payload: {
    application_token?: string;
    claim_token?: string;
    settlement_token?: string;
  } & DurableApplicationArtifactPayload,
): Promise<DurableApplicationDelivery & { context_version: number; settlement_acknowledged?: boolean }> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/api/application-deliveries/${deliveryId}/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw new DurableApplicationCommitError(
      (error as Error)?.message || 'Application commit transport failed.',
      'transport_unavailable',
      null,
      true,
    );
  }
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    throw new DurableApplicationCommitError(
      'Application commit response body could not be read.',
      'response_body_unavailable',
      response.status,
      response.ok || response.status >= 500,
    );
  }
  let decoded: any = null;
  try {
    decoded = raw ? JSON.parse(raw) : null;
  } catch {
    throw new DurableApplicationCommitError(
      'Application commit returned an unreadable response.',
      'invalid_response',
      response.status,
      response.ok || response.status >= 500,
    );
  }
  if (!response.ok) {
    const detail = decoded?.detail;
    throw new DurableApplicationCommitError(
      detail?.message || 'Application commit was rejected.',
      detail?.code || 'application_commit_rejected',
      response.status,
      response.status >= 500 || response.status === 408 || response.status === 429,
    );
  }
  if (!decoded || decoded.status !== 'applied' || typeof decoded.context_version !== 'number') {
    throw new DurableApplicationCommitError(
      'Application commit response did not prove durable settlement.',
      'invalid_response',
      response.status,
      true,
    );
  }
  return decoded;
}

export async function applyDurableApplicationDeliveryIdempotently(
  deliveryId: string,
  payload: Parameters<typeof applyDurableApplicationDelivery>[1],
  attempts = 2,
) {
  let lastError: unknown;
  for (let attempt = 0; attempt < Math.max(1, attempts); attempt += 1) {
    try {
      return await applyDurableApplicationDelivery(deliveryId, payload);
    } catch (error) {
      lastError = error;
      if (!(error instanceof DurableApplicationCommitError) || !error.retryable) throw error;
    }
  }
  throw lastError;
}

export async function sendAgentChatMessage(payload: AgentChatRequestPayload) {
  return apiCall('/api/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** The conversation already has a running turn: a recoverable wait, not a failure. */
export class ConversationTurnBusyError extends Error {
  readonly conversationId: string;
  readonly reason: 'turn_already_active' | 'campaign_specialist_lane_busy';

  constructor(
    conversationId: string,
    reason: 'turn_already_active' | 'campaign_specialist_lane_busy' = 'turn_already_active',
  ) {
    // Keeps the transport status in the message for callers that match it.
    super(`Streaming request failed with status 409: ${reason}`);
    this.name = 'ConversationTurnBusyError';
    this.conversationId = conversationId;
    this.reason = reason;
  }
}

/** A saved-work control whose durable checkpoint no longer exists or is resumable. */
export class ContinueNotEligibleError extends Error {
  readonly reason: string;

  constructor(reason: string) {
    super(`Streaming request failed with status 409: continue_not_eligible (${reason})`);
    this.name = 'ContinueNotEligibleError';
    this.reason = reason;
  }
}

export const isConversationTurnBusyError = (
  error: unknown,
): error is ConversationTurnBusyError => error instanceof ConversationTurnBusyError;

export const isContinueNotEligibleError = (
  error: unknown,
): error is ContinueNotEligibleError => error instanceof ContinueNotEligibleError;

const STREAM_RETRY_DELAY_MS = 1500;

export async function openAgentChatStream(payload: AgentChatRequestPayload) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: getAuthHeader(),
  };

  const request = () => fetch(`${STREAM_API_URL}/api/chat/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  let streamResponse: Response;
  try {
    streamResponse = await request();
  } catch (error) {
    // fetch rejects with a TypeError only when no HTTP response arrived at all
    // (production 2026-10-07: net::ERR_TIMED_OUT before the server recorded the
    // turn). Resend once on a fresh connection. If the first request did reach
    // the server, its turn is still running and the resend returns 409
    // turn_already_active, which callers already treat as "attach and wait".
    if (!(error instanceof TypeError)) throw error;
    await new Promise(resolve => setTimeout(resolve, STREAM_RETRY_DELAY_MS));
    streamResponse = await request();
  }

  if (!streamResponse.ok || !streamResponse.body) {
    if (streamResponse.status === 409) {
      const body = await streamResponse.json().catch(() => null) as
        | { detail?: { error?: unknown; conversation_id?: unknown } | string }
        | null;
      const detail = body?.detail;
      if (
        detail
        && typeof detail === 'object'
        && (
          detail.error === 'turn_already_active'
          || detail.error === 'campaign_specialist_lane_busy'
        )
      ) {
        const requested = (payload as { conversation_id?: unknown }).conversation_id;
        const reason = detail.error === 'campaign_specialist_lane_busy'
          ? 'campaign_specialist_lane_busy'
          : 'turn_already_active';
        throw new ConversationTurnBusyError(
          typeof detail.conversation_id === 'string'
            ? detail.conversation_id
            : String(requested ?? ''),
          reason,
        );
      }
      if (
        detail
        && typeof detail === 'object'
        && detail.error === 'continue_not_eligible'
      ) {
        throw new ContinueNotEligibleError(
          typeof (detail as { reason?: unknown }).reason === 'string'
            ? String((detail as { reason: string }).reason)
            : 'unknown',
        );
      }
    }
    throw new Error(`Streaming request failed with status ${streamResponse.status}`);
  }

  return streamResponse.body.getReader();
}

export interface CancelAgentTurnRequest {
  conversation_id: string;
  request_id: string;
  turn_id?: string;
}

export interface CancelAgentTurnResponse {
  turn_id?: string | null;
  terminal_status: string;
  reason_code: string;
  resumable: boolean;
  workflow_stage?: string;
  already_abandoned: boolean;
  lifecycle_phase: 'confirmed_terminal' | 'cleanup_pending';
}

export async function cancelAgentTurn(payload: CancelAgentTurnRequest): Promise<CancelAgentTurnResponse> {
  const response = await apiCall('/api/chat/cancel', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  // apiCall returns null for transport/HTTP failures, including a Stop that
  // races a finished turn. That is not a cancellation acknowledgement.
  // Reject it here so callers use their existing failure/reconcile path,
  // rather than dispatching null into the shared lifecycle reducer.
  if (
    !response || typeof response !== 'object'
    || !['confirmed_terminal', 'cleanup_pending'].includes(response.lifecycle_phase)
    || typeof response.terminal_status !== 'string'
    || typeof response.reason_code !== 'string'
    || typeof response.resumable !== 'boolean'
    || typeof response.already_abandoned !== 'boolean'
    || (response.turn_id != null && typeof response.turn_id !== 'string')
  ) {
    throw new Error('The server did not confirm the stop request.');
  }
  return response;
}

export async function openAppliedReviewStream(changeId: string) {
  const response = await fetch(`${STREAM_API_URL}/api/application-deliveries/reviews/${encodeURIComponent(changeId)}/stream`, {headers:{Authorization:getAuthHeader()}});
  if (!response.ok || !response.body) throw new Error('review_continuation_unavailable');
  return response.body.getReader();
}
